import React from 'react'
import Image from 'next/image'

import { Column, Layout, Section } from '@/components/layout'
import { Text, Icon, Link } from '@/components/common'
import TryFreeCta from '@/components/TryFreeCta'
import MobileAppFeatures from '@/components/blocks/MobileAppFeatures'
import receipts from '@/assets/ultra-guide/receipts.png'
import storeReceipts from '@/assets/ultra-guide/digitally-store-receipts.webp'
import mobileApp from '@/assets/ultra-guide/mobile-phone-app.png'
import taxPrep from '@/assets/ultra-guide/tax-prep.png'
import faqs from '@/assets/ultra-guide/faqs.webp'
import scannerApp from '@/assets/ultra-guide/scanner-app.webp'
import trackExpenses from '@/assets/ultra-guide/track-expenses.webp'
import { NEAT_DATA_URL } from '@/config'

import styles from './ultimate-guide-receipt-scanning-expense-tracking-document-management.module.scss'

const pageId = 'ultimateGuideReceiptScanningDocManagement'

const appFeatures = [
  {
    description: 'Take Control',
    moreInfo: 'Receipt & document capture. File import and with the knowledge you’ve gained from our guide.',
  },
  {
    description: 'Gain Clarity',
    moreInfo: 'Access, organize & share receipts & docs with our file management tools.',
  },
  {
    description: 'Move Forward with Confidence',
    moreInfo: 'Manage your records for taxes and beyond, even if you\'re not a financial expert.',
  },
]

const downloadChecklist = `${NEAT_DATA_URL}/documents/Neat_Tax_Deduction_Checklist.pdf`

export default function UltimateGuideReceiptScanningDocManagement() {
  return (
    <Layout.Content>
      <Section bgForeground paddingVertical={0} wrapperClassName={styles.wrapper}>
        <Column className="w-full">
          <div className={styles.heroHolder}>
            <div className="max-w-700">
              <Text bold brandSecondary className={styles.tagline} noMargin size="large" type="display">The Ultimate Guide to</Text>
              <Text.H1 bold normal onTint size="medium">Receipt Scanning, Expense Tracking, & Document Management for Small Businesses Owners & Solopreneurs</Text.H1>
            </div>
          </div>
        </Column>
      </Section>

      <Section paddingVertical={0} wrapperClassName={styles.pageIntro}>
        <div className={styles.pageIntroWrapper}>
          <div>
            This guide is your go-to resource if you're a solopreneur, freelancer, or small
            business owner overwhelmed by scattered receipts, manual tracking, or tax-time stress.
          </div>
          <div>
            <Text.P bold onTint>Our goal is to help you:</Text.P>
            <div className={styles.helpList}>
              <div>
                <ul>
                  <li>Understand what receipt and expense management really means</li>
                  <li>Choose the best tools (and avoid bloated software)</li>
                </ul>
              </div>
              <div>
                <ul>
                  <li>Build simple, scalable systems that save time and maximize tax deductions</li>
                  <li>Feel in control of your business finances</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <Text.H2 className="c-brand">Why Financial Organization is the Secret Weapon of Solopreneurs</Text.H2>

        <div className={styles.secretWeapon}>
          <div>
            <Text.P bold>Tame the Paper Trail: Finally Get Control of Your Business Finances</Text.P>
            <Text.P>If your receipts live in email folders, your glove talone. From solopreneurs to
              side hustlers, keeping track of every expense isn’t just frustrating—it’s time-consuming,
              stressful, and risky come tax season.
            </Text.P>
            <Text.P>That’s why we created this resource hub: to give you clear, simple, and smart strategies
              for organizing your receipts, managing expenses, and feeling in control of your business finances,
              without needing to be an accountant or spend hours in spreadsheets.
            </Text.P>
          </div>
          <div className={styles.rightSection}>
            <Text.H3 bold>Average</Text.H3>
            <Text.H3 bold>$11,638</Text.H3>
            <Text.P>in overpaid taxes by a small business for missing deductions because of not having proper documentation & organization.**</Text.P>
          </div>
        </div>
      </Section>

      <Section className={styles.financialOrganized}>
        <div className={styles.financialOrganizedWrapper}>
          <Text.H3 className="c-brand">What Happens When You Finally Get Financially Organized?</Text.H3>
          <Text.P>Getting your receipts and records under control doesn’t just reduce stress. It unlocks real business benefits:</Text.P>
          <Text.P><Icon fill="success" name="check" /> Maximize every tax deduction (without scrambling in April)</Text.P>
          <Text.P><Icon fill="success" name="check" /> See where your money’s going—so you can spend smarter</Text.P>
          <Text.P><Icon fill="success" name="check" /> Automate the boring stuff and get back to what you love</Text.P>
          <Text.P><Icon fill="success" name="check" /> Feel confident, polished, and professional with clients and partners</Text.P>
          <Text.P>You’ll gain more than peace of mind—you’ll gain time, clarity, and confidence. And we’ll show you how.</Text.P>
        </div>
      </Section>

      <Section>
        <div className={styles.chapterReceiptManagement}>
          <div>
            <Text.H3>Chapter 1: What Is Receipt Management and Why It Matters?</Text.H3>
            <Text.P><Link to="/blog/receipt-management-small-business">What is Receipt Management</Link></Text.P>
          </div>
          <div className={styles.imgSection} size={50}>
            <Image alt="Receipts" className={styles.dataExtractImg} src={receipts} style={{ height: 'auto' }} />
          </div>
        </div>
      </Section>

      <Section bgBrandTertiary>
        <div className={styles.chapterReceiptManagement}>
          <div>
            <Text.H3>Chapter 2: The Best Ways to Keep, Organize, and Digitally Store Receipts</Text.H3>
            <Text.P><Link to="/blog/how-to-find-the-best-receipt-management-app-for-freelancers">How to Find the Best Receipt Management App for Freelancers</Link></Text.P>
            <Text.P><Link to="/blog/how-to-organize-receipts-with-automation-save-hours-each-week">How to Organize Receipts with Automation</Link></Text.P>
          </div>
          <div className={styles.imgSection} size={50}>
            <Image alt="Receipts" className={styles.dataExtractImg} src={storeReceipts} style={{ height: 'auto' }} />
          </div>
        </div>
      </Section>

      <Section bgSecondary>
        <div className={styles.chapterReceiptManagement}>
          <div>
            <Text.H3>Chapter 3: How to Track Expenses Like a Pro (Without Using Spreadsheets)</Text.H3>
            <Text.P><Link to="/blog/expense-tracking-small-business">Expense Tracking for Small Business: A Simple System That Actually Works</Link></Text.P>
          </div>
          <div className={styles.imgSection} size={50}>
            <Image alt="Receipts" className={styles.dataExtractImg} src={trackExpenses} style={{ height: 'auto' }} />
          </div>
        </div>
      </Section>

      <Section>
        <div className={styles.chapterReceiptManagement}>
          <div>
            <Text.H3>Chapter 4: Top Apps for Receipt Scanning and Expense Tracking</Text.H3>
            <Text.P><Link to="/blog/how-to-find-the-best-receipt-management-app-for-freelancers">How to Find the Best Receipt Management App for Freelancers</Link></Text.P>
            <Text.P><Link to="/blog/receipt-scanning-app-with-ocr">Save Time and Stress with Scanning Apps Featuring OCR: A Must-Have for Freelancers</Link></Text.P>
          </div>
          <div className={styles.imgSection} size={50}>
            <Image alt="Receipts" className={styles.dataExtractImg} src={mobileApp} style={{ height: 'auto' }} />
          </div>
        </div>
      </Section>

      <Section bgBrandTertiary>
        <div className={styles.chapterReceiptManagement}>
          <div>
            <Text.H3>Chapter 5: Tax Season Prep — Maximize Deductions with Organized Receipts</Text.H3>
            <Text.P><Link to="/blog/irs-receipt-requirements-under-75">Does the IRS Require Receipts Under $75? Here's What You Need to Know</Link></Text.P>
            <Text.P><Link to="/blog/what-does-the-irs-consider-a-valid-receipt">What Does the IRS Consider a Valid Receipt? Paper vs Digital Receipts</Link></Text.P>
          </div>
          <div className={styles.imgSection} size={50}>
            <Image alt="Receipts" className={styles.dataExtractImg} src={taxPrep} style={{ height: 'auto' }} />
          </div>
        </div>
      </Section>

      <Section bgSecondary>
        <div className={styles.chapterReceiptManagement}>
          <div>
            <Text.H3>Chapter 6: FAQs — Answering Small Business Questions About Receipts & Expenses</Text.H3>
            <Text.P><Link to="/blog/are-pictures-of-receipts-okay-for-taxes-what-small-business-owners-need-to-know ">Are pictures of receipts okay for taxes?</Link></Text.P>
          </div>
          <div className={styles.imgSection} size={50}>
            <Image alt="Receipts" className={styles.dataExtractImg} src={faqs} style={{ height: 'auto' }} />
          </div>
        </div>
      </Section>

      <Section className={styles.mobileAppFeature}>
        <MobileAppFeatures appFeatures={appFeatures} image={scannerApp} title="Next steps to start tracking with our receipt scanner app" />
      </Section>

      <Section center className={styles.ctaSection}>
        <Text.H2>Easy and efficient – now that’s Neat!</Text.H2>
        <TryFreeCta />
        <Text.P>Neat is a yearly subscription where you can add on solutions.</Text.P>
      </Section>

      <Section center paddingBottom={0}>
        <Text.H2>Bonus Materials</Text.H2>

        <div className={styles.bonusMaterials}>
          <div className={styles.checklist}>
            <Text.H3>Tax Deduction Checklist (PDF)</Text.H3>

            <div>
              <Text.P>Stay organized and save money! Make sure you’re not missing out on valuable write-offs.</Text.P>

              <Link size="xlarge" to={downloadChecklist} variant="primary">Download Now</Link>
            </div>
          </div>
          <div className={styles.template}>
            <Text.H3>Free Expense Spreadsheet Template</Text.H3>

            <div>
              <Text.P>Track spending and stay on budget to take control of your finances.</Text.P>

              <Link external openTab size="xlarge" to="https://docs.google.com/spreadsheets/d/1FGqBBo49S2-a0A5BCOJjhDYUTdlaa7R9bGIsXFXUDjc/copy" variant="primary">Download Now</Link>
            </div>
          </div>
        </div>
      </Section>

      <Section center className={styles.ctaSection}>
        <Text.P size="xsmall">** Based on study by Treasury Inspector General for Tax Administration (TIGTA) and National
          Society of Accountants (NSA) post. Actual overpayment may vary significantly based on individual business
          activity, eligible expenses, and tax situation.
        </Text.P>
      </Section>
    </Layout.Content>
  )
}

UltimateGuideReceiptScanningDocManagement.layoutProps = {
  pageId,
}
